import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Reveal from "@/components/Reveal";
import SmartVideo from "@/components/SmartVideo";
import Seo from "@/components/Seo";
import { FinalCTA } from "@/pages/Home";
import { api } from "@/lib/api";
import { useSiteContent } from "@/lib/content";
import { useAuth } from "@/context/AuthContext";
import { Plus } from "lucide-react";

const REEL_VIDEO = "https://videos.pexels.com/video-files/3141210/3141210-uhd_2560_1440_25fps.mp4";
const REEL_POSTER = "https://images.unsplash.com/photo-1478720568477-152d9b164e26?q=80&w=1600&auto=format&fit=crop";

const SEO_META = {
  product: {
    title: "Fotografia Product & Still Life — Nicola Verardo",
    desc: "Still life, packshot evoluti e campagne prodotto: luce controllata e composizione rigorosa per costruire il valore percepito del prodotto.",
  },
  architecture: {
    title: "Fotografia Architettura & Interni — Nicola Verardo",
    desc: "Fotografia di architettura e interni per studi, privati ed editoria: prospettive corrette, luce naturale, l'atmosfera vera del progetto.",
  },
  food: {
    title: "Fotografia Food — Nicola Verardo Visual Production",
    desc: "Fotografia food per ristoranti, chef e brand alimentari: materia prima protagonista, immagini che costruiscono il valore del brand.",
  },
  "motion-ai": {
    title: "Motion, Video & AI — Nicola Verardo Visual Production",
    desc: "Video, reel e campagne ibride: il prodotto resta fotograficamente autentico, l'AI estende ciò che un set tradizionale non può costruire.",
  },
};

const DEFAULTS = {
  product: {
    label: "Product & Still Life",
    title: "Il prodotto, elevato a oggetto di desiderio.",
    desc: "Still life, packshot evoluti e campagne prodotto. Luce controllata, composizione rigorosa, materiali e texture al centro. La fotografia non mostra semplicemente un prodotto: ne costruisce il valore percepito.",
    note: "80% della produzione",
  },
  architecture: {
    label: "Architecture & Interiors",
    title: "Spazi raccontati nell'ora giusta.",
    desc: "Fotografia di architettura e interni per studi, committenze private ed editoria. Prospettive corrette, luce naturale integrata, nessun effetto da portale immobiliare: solo l'atmosfera vera del progetto.",
    note: "Area in crescita",
  },
  food: {
    label: "Food & Culinary",
    title: "Il cibo come oggetto di desiderio.",
    desc: "Fotografia food per ristoranti, chef e brand alimentari. Luce controllata, materia prima protagonista: immagini che fanno venire fame e costruiscono il valore del brand.",
    note: "Produzione selezionata",
  },
  "motion-ai": {
    label: "Motion / AI / Video",
    title: "Produzione visiva evoluta.",
    desc: "Video, reel e campagne ibride dove la fotografia reale incontra ambientazioni generate con l'AI. Il prodotto resta fotograficamente autentico; la tecnologia estende ciò che un set tradizionale non può costruire.",
    note: "L'AI come vantaggio produttivo, non come effetto",
  },
};

const Category = ({ category }) => {
  const fallback = DEFAULTS[category];
  const prefix = category === "motion-ai" ? "motion" : category;
  const content = useSiteContent();
  const tx = (key, fb) => content?.texts?.[`cat_${prefix}_${key}`] || fb;
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [reelOpen, setReelOpen] = useState(false);
  const reelPoster = content?.reel_poster || REEL_POSTER;
  const reelVideo = content?.reel_video || REEL_VIDEO;

  useEffect(() => {
    api.get(`/projects?category=${category}`).then((res) => setProjects(res.data)).catch(() => {});
  }, [category]);

  return (
    <main data-testid={`category-page-${category}`} className="bg-night pt-32">
      <div className="px-6 sm:px-12 lg:px-20">
        <Reveal>
          <Seo
            title={SEO_META[category].title}
            description={SEO_META[category].desc}
            path={`/${category}`}
          />
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold">{tx("label", fallback.label)}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-bone max-w-3xl">
            {tx("title", fallback.title)}
          </h1>
          <p className="mt-6 text-base text-fog leading-relaxed max-w-xl">{tx("desc", fallback.desc)}</p>
          <p className="mt-4 text-[10px] font-mono tracking-[0.25em] text-fog/60 uppercase">{tx("note", fallback.note)}</p>
        </Reveal>
      </div>

      {category === "motion-ai" && (
        <div className="px-6 sm:px-12 lg:px-20 mt-16">
          <Reveal>
            <button
              onClick={() => setReelOpen(true)}
              data-testid="category-reel-button"
              className="relative block w-full group text-left"
              aria-label="Riproduci showreel"
            >
              <img src={reelPoster} alt="Showreel" loading="lazy" className="w-full aspect-[21/9] object-cover opacity-90" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-16 h-16 rounded-full bg-bone flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Play size={20} className="ml-1 text-night" />
                </span>
              </span>
              <span className="absolute bottom-5 left-6 text-[10px] font-mono tracking-[0.25em] text-bone">SHOWREEL — 00:30</span>
            </button>
          </Reveal>
        </div>
      )}

      <div className="px-6 sm:px-12 lg:px-20 mt-16 pb-24 grid md:grid-cols-2 gap-6">
        {projects.map((project, i) => (
          <Reveal key={project.slug} delay={(i % 2) * 120}>
            <Link to={`/work/${project.slug}`} data-testid={`category-project-${project.slug}`} className="group block">
              <div className="overflow-hidden bg-surface">
                <img
                  src={project.hero}
                  alt={project.title}
                  loading="lazy"
                  className="w-full aspect-[16/9] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>
              <div className="mt-4 flex items-baseline justify-between gap-4">
                <div>
                  <h2 className="text-lg font-medium tracking-tight text-bone">{project.title}</h2>
                  <p className="mt-1 text-xs font-mono tracking-[0.2em] text-fog uppercase">{project.client} — {project.year}</p>
                </div>
                <ArrowRight size={16} className="shrink-0 text-fog group-hover:text-gold group-hover:translate-x-1 transition-[color,transform] duration-300" />
              </div>
            </Link>
          </Reveal>
        ))}
        {user && (
          <Link
            to={`/admin/progetti/nuovo?categoria=${category}`}
            data-testid="add-project-button"
            className="group flex flex-col items-center justify-center gap-3 border border-dashed border-line aspect-[16/9] text-fog hover:text-gold hover:border-gold transition-[color,border-color] duration-300"
          >
            <Plus size={28} />
            <span className="text-xs font-mono tracking-[0.25em]">AGGIUNGI NUOVO</span>
          </Link>
        )}
      </div>

      <Dialog open={reelOpen} onOpenChange={setReelOpen}>
        <DialogContent className="max-w-5xl w-[92vw] bg-night border-line p-0 overflow-hidden">
          {reelOpen && <SmartVideo url={reelVideo} poster={reelPoster} />}
        </DialogContent>
      </Dialog>

      <FinalCTA />
    </main>
  );
};

export default Category;
